import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableContainer,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add,
  CheckCircle,
  Cancel,
  DeleteOutline,
  EditOutlined,
  GroupOutlined,
  SearchRounded,
} from '@mui/icons-material';
import { getClientes, deleteCliente, getRestaurantes } from '../services/api';

const PAGE_BG = `
  radial-gradient(ellipse at 0% 0%, rgba(99, 102, 241, 0.07), transparent 45%),
  radial-gradient(ellipse at 100% 0%, rgba(124, 58, 237, 0.05), transparent 45%),
  linear-gradient(180deg, #f7f9ff 0%, #f8fafc 60%, #f7f9ff 100%)
`;

const CARD_SX = {
  borderRadius: '20px',
  border: '1px solid rgba(15, 23, 42, 0.06)',
  boxShadow: '0 14px 36px -14px rgba(15, 23, 42, 0.10), 0 0 0 1px rgba(15, 23, 42, 0.02)',
  background: '#ffffff',
  overflow: 'hidden',
};

const SECTION_ICON_BOX_SX = {
  width: 40,
  height: 40,
  borderRadius: '12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'linear-gradient(135deg, rgba(99,102,241,0.14) 0%, rgba(124,58,237,0.10) 100%)',
  color: '#6366f1',
  flexShrink: 0,
  border: '1px solid rgba(99,102,241,0.18)',
};

const AVATAR_PALETTE = ['#6366f1', '#0891b2', '#059669', '#d97706', '#7c3aed', '#db2777', '#0f766e'];
const getAvatarColor = (name = '') =>
  AVATAR_PALETTE[(name.charCodeAt(0) || 0) % AVATAR_PALETTE.length];

const badgeChipSx = {
  height: 26,
  fontWeight: 700,
  fontSize: '0.72rem',
  borderRadius: '999px',
  border: '1px solid transparent',
  '& .MuiChip-label': { px: 1.1 },
};

const ClientesTable = ({
  items, onEdit, onDelete, getRestauranteNome,
  count, page, pageSize, onPageChange, onRowsPerPageChange,
}) => {
  const headCells = [
    { id: 'nome',        label: 'Nome',            align: 'left'                },
    { id: 'telefone',    label: 'Telefone',         align: 'left',  width: '15%' },
    { id: 'restaurante', label: 'Estabelecimento',  align: 'left',  width: '20%' },
    { id: 'transbordo',  label: 'Transbordo',       align: 'left',  width: '12%' },
    { id: 'ativo',       label: 'Ativo',            align: 'left',  width: '10%' },
    { id: 'acoes',       label: 'Ações',            align: 'right', width: '10%' },
  ];

  return (
    <Box sx={CARD_SX}>
      <TableContainer sx={{ px: { xs: 1, md: 1.5 }, pt: 1.5, pb: 0 }}>
        <Table sx={{ minWidth: 860, tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f8fafc' }}>
              {headCells.map((hc) => (
                <TableCell
                  key={hc.id}
                  align={hc.align}
                  sx={{ width: hc.width, borderBottom: '1px solid #eaecf0', py: 1.75, px: 2.25, fontSize: '0.775rem', fontWeight: 700, color: '#475467', letterSpacing: '0.01em' }}
                >
                  {hc.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {items.length === 0 && (
              <TableRow>
                <TableCell colSpan={headCells.length} align="center" sx={{ py: 9, border: 0 }}>
                  <Typography sx={{ color: '#94a3b8', fontWeight: 500, fontSize: '0.9rem' }}>
                    Nenhum cliente encontrado.
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {items.map((it) => {
              const avatarColor = getAvatarColor(it.nome);
              const initials = (it.nome || '?').slice(0, 2).toUpperCase();

              return (
                <TableRow
                  key={it.id}
                  sx={{
                    '& td': {
                      py: 1.75, px: 2.25,
                      borderBottom: '1px solid rgba(15,23,42,0.05)',
                      verticalAlign: 'middle',
                      bgcolor: '#fff',
                      transition: 'background-color 140ms ease',
                    },
                    '& td:first-of-type': { borderLeft: '3px solid transparent', transition: 'border-left-color 140ms ease' },
                    '&:hover td': { bgcolor: '#fafbff' },
                    '&:hover td:first-of-type': { borderLeftColor: '#c7d2fe' },
                    '&:last-child td': { borderBottom: 0 },
                  }}
                >
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={1.25}>
                      <Box sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: avatarColor, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '0.67rem', fontWeight: 800, flexShrink: 0, letterSpacing: '0.03em' }}>
                        {initials}
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.875rem', color: '#101828', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {it.nome}
                      </Typography>
                    </Stack>
                  </TableCell>

                  <TableCell>
                    <Typography variant="body2" sx={{ color: '#475467', fontWeight: 500, fontSize: '0.875rem' }}>
                      {it.telefone || '—'}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Typography variant="body2" sx={{ color: '#475467', fontWeight: 500, fontSize: '0.875rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {getRestauranteNome(it.restaurante_id)}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Chip
                      label={it.transbordo ? 'Sim' : 'Não'}
                      size="small"
                      icon={it.transbordo
                        ? <CheckCircle sx={{ fontSize: '14px !important', ml: '6px !important' }} />
                        : <Cancel sx={{ fontSize: '14px !important', ml: '6px !important' }} />}
                      sx={{
                        ...badgeChipSx,
                        bgcolor: it.transbordo ? '#eef2ff' : '#f8fafc',
                        color: it.transbordo ? '#4338ca' : '#64748b',
                        borderColor: it.transbordo ? '#c7d2fe' : '#eaecf0',
                      }}
                    />
                  </TableCell>

                  <TableCell>
                    <Switch
                      checked={!!it.active}
                      color="success"
                      size="small"
                      readOnly
                      sx={{
                        '& .MuiSwitch-thumb': { boxShadow: 'none' },
                        pointerEvents: 'none',
                      }}
                    />
                  </TableCell>

                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <Tooltip title="Editar">
                        <IconButton
                          size="small"
                          onClick={() => onEdit(it)}
                          aria-label={`Editar cliente ${it.nome}`}
                          sx={{ width: 34, height: 34, borderRadius: '10px', border: '1px solid #c7d2fe', bgcolor: '#eef2ff', color: '#4338ca', '&:hover': { bgcolor: '#e0e7ff' }, transition: 'all 140ms ease' }}
                        >
                          <EditOutlined sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Excluir">
                        <IconButton
                          size="small"
                          onClick={() => onDelete(it.id)}
                          aria-label={`Excluir cliente ${it.nome}`}
                          sx={{ width: 34, height: 34, borderRadius: '10px', border: '1px solid #fecdd3', bgcolor: '#fff1f2', color: '#be123c', '&:hover': { bgcolor: '#ffe4e6' }, transition: 'all 140ms ease' }}
                        >
                          <DeleteOutline sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={count}
        page={page}
        onPageChange={onPageChange}
        rowsPerPage={pageSize}
        onRowsPerPageChange={onRowsPerPageChange}
        rowsPerPageOptions={[10, 20, 50, 100]}
        sx={{
          borderTop: '1px solid rgba(15,23,42,0.06)',
          color: '#64748b',
          fontSize: '0.85rem',
          '& .MuiTablePagination-select': { borderRadius: '10px' },
          '& .MuiTablePagination-actions button': { borderRadius: '10px', '&:hover': { bgcolor: '#eef2ff' } },
        }}
      />
    </Box>
  );
};

const ClientesPage = () => {
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState('');
  const [restaurantes, setRestaurantes] = useState([]);
  const [restauranteId, setRestauranteId] = useState('');
  const [transbordo, setTransbordo] = useState('');
  const [active, setActive] = useState('');
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const res = await getClientes({
        page: page + 1,
        pageSize,
        search,
        restaurante_id: restauranteId || undefined,
        transbordo: transbordo !== '' ? transbordo : undefined,
        active: active !== '' ? active : undefined,
      });
      setItems(res.data);
      setCount(res.count);
    } catch (err) {
      console.error(err);
    }
  }, [page, pageSize, search, restauranteId, transbordo, active]);

  useEffect(() => {
    const loadRestaurantes = async () => {
      const data = await getRestaurantes();
      setRestaurantes(data);
    };
    loadRestaurantes();
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleEdit = (it) => navigate(`/manager/clientes/${it.id}`, { state: it });
  const handleDelete = async (id) => {
    if (!window.confirm('Excluir registro?')) return;
    await deleteCliente(id);
    load();
  };
  const getRestauranteNome = (id) =>
    restaurantes.find((r) => String(r.id) === String(id))?.nome || '-';

  const hasActiveFilters = Boolean(search || restauranteId || transbordo || active);

  return (
    <Box sx={{ minHeight: '100vh', background: PAGE_BG, py: { xs: 3, md: 4 } }}>
      <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2, md: 3 } }}>
        <Stack spacing={2.5}>

          {/* ── Header card ── */}
          <Box sx={CARD_SX}>
            <Box sx={{ height: 4, background: 'linear-gradient(90deg, #6366f1 0%, #8b5cf6 60%, #a78bfa 100%)' }} />
            <Box sx={{ p: { xs: 2.5, md: 3 } }}>
              <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between" gap={2} mb={2.5}>
                <Stack direction="row" alignItems="center" gap={1.5}>
                  <Box sx={SECTION_ICON_BOX_SX}>
                    <GroupOutlined sx={{ fontSize: 22 }} />
                  </Box>
                  <Box>
                    <Typography fontWeight={800} sx={{ color: '#0f172a', fontSize: { xs: '1.25rem', md: '1.4rem' }, letterSpacing: '-0.02em', lineHeight: 1.2, mb: 0.25 }}>
                      Clientes Atendabot
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748b' }}>
                      Veja, filtre, edite ou exclua os clientes do sistema.
                    </Typography>
                  </Box>
                </Stack>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => navigate('/manager/clientes/new')}
                  sx={{
                    borderRadius: '12px', px: 2.5, py: 1, fontWeight: 700, boxShadow: 'none', letterSpacing: '0.01em', flexShrink: 0,
                    bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5', boxShadow: '0 4px 14px rgba(99,102,241,0.3)' },
                    transition: 'all 150ms ease',
                  }}
                >
                  Novo Cliente
                </Button>
              </Stack>

              <Divider sx={{ mb: 2.5, borderColor: 'rgba(15,23,42,0.06)' }} />

              {/* Total count */}
              <Box sx={{ textAlign: 'center' }}>
                <Typography sx={{ fontSize: { xs: '1.6rem', md: '2rem' }, fontWeight: 800, color: '#0f172a', lineHeight: 1.15, mb: 0.25 }}>
                  {count}
                </Typography>
                <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8' }}>
                  {hasActiveFilters ? 'Clientes encontrados' : 'Clientes cadastrados'}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* ── Filters card ── */}
          <Box sx={{ ...CARD_SX, overflow: 'visible' }}>
            <Box sx={{ p: { xs: 1.75, md: 2.25 } }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems={{ xs: 'stretch', sm: 'center' }} flexWrap="wrap" useFlexGap>
                <TextField
                  label="Buscar"
                  size="small"
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                  InputProps={{ startAdornment: <InputAdornment position="start"><SearchRounded fontSize="small" color="action" /></InputAdornment> }}
                  sx={{ flexGrow: 1, minWidth: 200, '& .MuiOutlinedInput-root': { height: 42, bgcolor: '#fff', borderRadius: '12px' } }}
                />
                <TextField
                  select label="Estabelecimento" size="small" value={restauranteId}
                  onChange={(e) => { setRestauranteId(e.target.value); setPage(0); }}
                  sx={{ minWidth: 180, '& .MuiOutlinedInput-root': { height: 42, bgcolor: '#fff', borderRadius: '12px' } }}
                >
                  <MenuItem value="">Todos</MenuItem>
                  {restaurantes.map((r) => <MenuItem key={r.id} value={r.id}>{r.nome}</MenuItem>)}
                </TextField>
                <TextField
                  select label="Transbordo" size="small" value={transbordo}
                  onChange={(e) => { setTransbordo(e.target.value); setPage(0); }}
                  sx={{ minWidth: 140, '& .MuiOutlinedInput-root': { height: 42, bgcolor: '#fff', borderRadius: '12px' } }}
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="true">Sim</MenuItem>
                  <MenuItem value="false">Não</MenuItem>
                </TextField>
                <TextField
                  select label="Ativo" size="small" value={active}
                  onChange={(e) => { setActive(e.target.value); setPage(0); }}
                  sx={{ minWidth: 140, '& .MuiOutlinedInput-root': { height: 42, bgcolor: '#fff', borderRadius: '12px' } }}
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="true">Sim</MenuItem>
                  <MenuItem value="false">Não</MenuItem>
                </TextField>
              </Stack>
            </Box>
          </Box>

          {/* ── Table ── */}
          <ClientesTable
            items={items}
            onEdit={handleEdit}
            onDelete={handleDelete}
            getRestauranteNome={getRestauranteNome}
            count={count}
            page={page}
            pageSize={pageSize}
            onPageChange={(e, p) => setPage(p)}
            onRowsPerPageChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
          />
        </Stack>
      </Box>
    </Box>
  );
};

export default ClientesPage;
